import {
  CodeAndLabel,
  SecondaryDischargeReason,
} from '~/store/services/clinical-data/refDischargePlan';
import { DischargePlanStatuses } from '../common/dischargePlanConstants';
import { GetDischargePlanResponse } from '~/store/services/clinical-data/dischargePlan';
import { DischargeDetailsErrorFields } from '../DischargePlanViewContext';
import { PatientUniversalStatusCode } from '~/lib/constants/patients.constants';

export const getDischargeStatuses = (status: CodeAndLabel) => {
  return {
    isNotStarted: status?.code === DischargePlanStatuses.NOT_STARTED.code,
    isPrepping: status?.code === DischargePlanStatuses.PREPPING.code,
    isSigned: status?.code === DischargePlanStatuses.SIGNED.code,
  };
};

export const getRequiredDischargeDetailsFieldErrors = (
  dischargePlanData: GetDischargePlanResponse,
  secondaryReasonsData?: SecondaryDischargeReason[],
): DischargeDetailsErrorFields => {
  const isPatientSeekingCare =
    dischargePlanData?.isPatientSeekingCare?.code === 'YES';

  // Check if primary has associated secondary reasons
  const primaryHasSecondaryReasons =
    secondaryReasonsData?.some(
      (sr) =>
        sr.primaryDischargeReason?.statusReasonExternalId ===
        dischargePlanData.primaryDischargeReason?.statusReasonExternalId,
    ) ?? false;

  return {
    isPatientSeekingCare: !dischargePlanData.isPatientSeekingCare,
    levelOfCare:
      isPatientSeekingCare &&
      (!dischargePlanData.levelOfCare ||
        dischargePlanData.levelOfCare.length === 0),
    primaryDischargeReason: !dischargePlanData.primaryDischargeReason,
    secondaryDischargeReason:
      primaryHasSecondaryReasons && !dischargePlanData.secondaryDischargeReason,
    tentativeDischargeDate: !dischargePlanData.tentativeDischargeDate,
    typeOfCare:
      isPatientSeekingCare &&
      (!dischargePlanData.typeOfCare ||
        dischargePlanData.typeOfCare.length === 0),
    whoInitiatedDischarge: !dischargePlanData.whoInitiatedDischarge,
  };
};

export const getAutoSelectedSecondaryReason = (
  primaryReasonExternalId: string | undefined,
  secondaryReasonsData: SecondaryDischargeReason[] | undefined,
): SecondaryDischargeReason | null => {
  if (!primaryReasonExternalId || !secondaryReasonsData) {
    return null;
  }

  const matchingSecondaryReasons = secondaryReasonsData.filter(
    (sr) =>
      sr.primaryDischargeReason?.statusReasonExternalId ===
      primaryReasonExternalId,
  );

  return matchingSecondaryReasons.length === 1
    ? matchingSecondaryReasons[0]
    : null;
};

export const getPreppingTasks = (tasks?: TaskSummary[]) =>
  tasks?.filter((task) => task.type === DischargePlanStatuses.PREPPING.code) ??
  [];

export type SelectedEpisodeResult = {
  episode: TreatmentEpisode | undefined;
  isDischarged: boolean;
};

export const selectDischargePlanEpisode = (
  episodes: TreatmentEpisode[],
): SelectedEpisodeResult => {
  const inTreatment = episodes.find(
    (ep) => ep.status?.code === PatientUniversalStatusCode.IN_TREATMENT,
  );
  if (inTreatment) {
    return { episode: inTreatment, isDischarged: false };
  }

  const dischargedEpisodes = episodes
    .filter((ep) => ep.status?.code === PatientUniversalStatusCode.DISCHARGED)
    .sort((a, b) => {
      const dateA = a.dischargeDate ?? a.createdAt ?? '';
      const dateB = b.dischargeDate ?? b.createdAt ?? '';
      return dateB.localeCompare(dateA);
    });

  if (dischargedEpisodes.length > 0) {
    return { episode: dischargedEpisodes[0], isDischarged: true };
  }

  return { episode: undefined, isDischarged: false };
};
